import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import { ShieldCheck, LogOut, Loader2, MapPin, MonitorSmartphone, Ticket, Plus, Send, ArrowLeft, LifeBuoy, ShoppingCart, Undo2, Building2, Search, Server, Wifi, WifiOff, Activity, Clock, CircleAlert, LayoutDashboard, ChevronRight } from "lucide-react";
import { API } from "@/lib/api";
import { timeAgo } from "@/lib/time";

const FIELD_LABELS = {
  device_code: "Geräte-Code", location_name: "Standort", status: "Status", asset_tag: "AssetID",
  tablet_serial: "Seriennummer", teamviewer_id: "TeamViewer-ID", teamviewer_version: "TV-Version",
  hostname: "Hostname", model: "Modell", manufacturer: "Hersteller", os_version: "Betriebssystem",
  agent_installed: "Agent", last_seen: "Zuletzt gesehen", first_seen: "Erstkontakt",
};
const CK = "cportal_jwt";
const inp = "w-full bg-white/[0.04] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#c00000]/60 focus:border-transparent transition";
const card = "bg-gray-50 border border-gray-200 rounded-2xl";

function getCtoken() { return new URLSearchParams(window.location.search).get("ctoken") || ""; }

export default function SecurityPortal() {
  const ctoken = getCtoken();
  const [jwt, setJwt] = useState(() => localStorage.getItem(CK) || "");
  const authed = !!ctoken || !!jwt;

  const cfetch = useCallback(async (path, opts = {}) => {
    const url = new URL(`${API}${path}`);
    if (ctoken) url.searchParams.set("ctoken", ctoken);
    const headers = { ...(opts.body ? { "Content-Type": "application/json" } : {}) };
    if (!ctoken && jwt) headers.Authorization = `Bearer ${jwt}`;
    const r = await fetch(url.toString(), { ...opts, headers });
    if (r.status === 401) { if (!ctoken) { localStorage.removeItem(CK); setJwt(""); } throw new Error("Zugang abgelaufen – bitte neu anmelden."); }
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Fehler");
    return r.json();
  }, [ctoken, jwt]);

  if (!authed) return <Login onToken={(t) => { localStorage.setItem(CK, t); setJwt(t); }} />;
  return <PortalShell cfetch={cfetch} onLogout={() => { localStorage.removeItem(CK); setJwt(""); window.location.href = "/security-portal"; }} tokenMode={!!ctoken} />;
}

function Login({ onToken }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await fetch(`${API}/cportal/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: email.trim().toLowerCase(), password }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Login fehlgeschlagen");
      const d = await r.json(); onToken(d.token);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-white bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#c00000]/10 via-white to-white flex items-center justify-center p-4">
      <Toaster theme="dark" position="top-center" richColors />
      <div className="w-full max-w-md bg-gray-50 border border-gray-200 rounded-2xl p-8 shadow-2xl">
        <div className="flex items-center justify-center gap-2 mb-6 p-3 rounded-xl bg-[#c00000]/10 border border-[#c00000]/30">
          <ShieldCheck className="w-6 h-6 text-[#c00000]" /><span className="text-lg font-bold text-gray-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Security-Portal</span>
        </div>
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Willkommen zurück</h1>
        <p className="text-sm text-gray-600 mb-6">Melden Sie sich an, um Ihre Standorte und Geräte in Echtzeit zu überwachen.</p>
        <form onSubmit={submit} className="space-y-4">
          <div><label className="text-[11px] font-mono uppercase tracking-widest text-gray-600 mb-1.5 block">E-Mail</label>
            <input data-testid="cportal-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inp} required /></div>
          <div><label className="text-[11px] font-mono uppercase tracking-widest text-gray-600 mb-1.5 block">Passwort</label>
            <input data-testid="cportal-login-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inp} required /></div>
          <button data-testid="cportal-login-submit" disabled={busy} className="w-full flex items-center justify-center gap-2 bg-[#c00000] hover:bg-[#c00000] text-[#06121a] font-bold rounded-xl px-4 py-3.5 text-sm transition-colors disabled:opacity-50 shadow-[0_0_20px_rgba(6,182,212,0.15)]">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Sicher anmelden</button>
        </form>
        <p className="text-[11px] text-gray-600 text-center mt-6 font-mono">Verschlüsselte Verbindung · Fleet Security</p>
      </div>
    </div>
  );
}

function PortalShell({ cfetch, onLogout, tokenMode }) {
  const [session, setSession] = useState(null);
  const [view, setView] = useState("overview");
  const [err, setErr] = useState("");
  useEffect(() => { cfetch("/cportal/session").then(setSession).catch((e) => setErr(e.message)); }, [cfetch]);

  if (err) return <Center><div className="text-rose-300 flex items-center gap-2"><CircleAlert className="w-5 h-5" /> {err}</div></Center>;
  if (!session) return <Center><Loader2 className="w-6 h-6 animate-spin text-[#c00000]" /></Center>;

  const tabs = [["overview", "Übersicht", LayoutDashboard], ["devices", "Standorte & Geräte", MonitorSmartphone], ["tickets", "Meine Anfragen", Ticket], ["new", "Neue Anfrage", Plus]];
  return (
    <div className="min-h-screen bg-white text-gray-800" style={{ fontFamily: "Inter, sans-serif" }}>
      <Toaster theme="dark" position="top-center" richColors />
      <header className="sticky top-0 z-30 bg-gray-50/95 backdrop-blur-xl border-b border-gray-200 shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#c00000]/10 border border-[#c00000]/30 flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5 text-[#c00000]" /></div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-gray-900 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{session.name}</div>
              <div className="text-[11px] text-gray-600 font-mono flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> Security-Portal · {session.location_count} Standorte</div>
            </div>
          </div>
          {!tokenMode && <button data-testid="cportal-logout" onClick={onLogout} className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 transition-colors"><LogOut className="w-3.5 h-3.5" /> Abmelden</button>}
        </div>
        <div className="max-w-7xl mx-auto px-2 sm:px-4 flex gap-0.5 sm:gap-1 -mb-px overflow-x-auto">
          {tabs.map(([id, lbl, Icon]) => (
            <button key={id} data-testid={`cportal-tab-${id}`} onClick={() => setView(id)} className={`flex items-center gap-1.5 text-sm px-3 sm:px-4 py-3 border-b-2 whitespace-nowrap transition-colors ${view === id ? "border-[#c00000] text-gray-900 font-semibold" : "border-transparent text-gray-600 hover:text-gray-800"}`}><Icon className="w-4 h-4" /> {lbl}</button>
          ))}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 py-6 animate-in fade-in-50 duration-150">
        {view === "overview" && <OverviewView cfetch={cfetch} onGoDevices={(lid) => { window.__cpLoc = lid; setView("devices"); }} onNew={() => setView("new")} />}
        {view === "devices" && <DevicesView cfetch={cfetch} fields={session.visible_fields} />}
        {view === "tickets" && <TicketsView cfetch={cfetch} />}
        {view === "new" && <NewRequestView cfetch={cfetch} onDone={() => setView("tickets")} />}
      </main>
      <footer className="max-w-7xl mx-auto px-4 py-6 text-center text-[11px] text-gray-600 font-mono">Fleet Security Monitoring · Live-Daten</footer>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-screen bg-white flex items-center justify-center">{children}</div>;

function RatioBar({ online, total }) {
  const pct = total ? Math.round((online / total) * 100) : 0;
  return (
    <div>
      <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden flex">
        <div className="bg-green-500 h-full transition-all duration-500" style={{ width: `${pct}%` }} />
        <div className="bg-rose-500/80 h-full transition-all duration-500" style={{ width: `${100 - pct}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1.5 text-[11px] font-mono">
        <span className="text-green-600">{online} online</span>
        <span className="text-gray-600">{pct}%</span>
        <span className="text-rose-400">{total - online} offline</span>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent = "cyan" }) {
  const map = { cyan: "text-[#c00000] bg-[#c00000]/10 border-[#c00000]/30", emerald: "text-green-600 bg-green-500/10 border-green-500/30", amber: "text-amber-600 bg-amber-500/10 border-amber-500/30", rose: "text-rose-400 bg-rose-500/10 border-rose-500/30" };
  return (
    <div className={`${card} p-5 bg-gradient-to-b from-white/[0.04] to-white/[0.01]`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-mono uppercase tracking-widest text-gray-600">{label}</span>
        <span className={`w-8 h-8 rounded-lg border flex items-center justify-center ${map[accent]}`}><Icon className="w-4 h-4" /></span>
      </div>
      <div className="text-3xl font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
      {sub && <div className="text-xs text-gray-600 mt-1">{sub}</div>}
    </div>
  );
}

function OverviewView({ cfetch, onGoDevices, onNew }) {
  const [d, setD] = useState(null);
  useEffect(() => { cfetch("/cportal/overview").then(setD).catch(() => setD({})); }, [cfetch]);
  if (!d) return <Loader2 className="w-6 h-6 animate-spin text-[#c00000]" />;
  const issues = (d.locations || []).filter((l) => l.total > l.online).length;
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Übersicht</h2>
        <p className="text-sm text-gray-600">Live-Status aller Ihnen zugewiesenen Standorte und Geräte.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Server} label="Geräte gesamt" value={d.total_devices ?? 0} sub={`über ${d.location_count ?? 0} Standorte`} accent="cyan" />
        <div className={`${card} p-5 bg-gradient-to-b from-white/[0.04] to-white/[0.01]`}>
          <div className="flex items-center justify-between mb-3"><span className="text-[11px] font-mono uppercase tracking-widest text-gray-600">Verfügbarkeit</span><span className="w-8 h-8 rounded-lg border flex items-center justify-center text-green-600 bg-green-500/10 border-green-500/30"><Activity className="w-4 h-4" /></span></div>
          <RatioBar online={d.online ?? 0} total={d.total_devices ?? 0} />
        </div>
        <Kpi icon={MapPin} label="Standorte" value={d.location_count ?? 0} sub={issues ? `${issues} mit Offline-Geräten` : "Alle Geräte online"} accent={issues ? "amber" : "emerald"} />
        <Kpi icon={Ticket} label="Offene Anfragen" value={d.open_tickets ?? 0} sub={d.pending_tickets ? `${d.pending_tickets} warten auf Freigabe` : "keine Freigabe offen"} accent={d.open_tickets ? "amber" : "emerald"} />
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>Standort-Zustand</h3>
        <button data-testid="cportal-overview-new" onClick={onNew} className="flex items-center gap-1.5 text-sm bg-[#c00000]/15 border border-[#c00000]/40 text-[#c00000] rounded-lg px-3 py-2 hover:bg-[#c00000]/25 transition-colors"><Plus className="w-4 h-4" /> Neue Anfrage</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {(d.locations || []).map((l) => (
          <button key={l.location_id} data-testid={`cportal-ov-loc-${l.location_id}`} onClick={() => onGoDevices(l.location_id)} className={`${card} p-4 text-left hover:border-[#c00000]/30 hover:bg-gray-50 transition-all duration-200 group`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-xs text-[#c00000]/90 truncate">{l.location_id}</span>
              <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-[#c00000] transition-colors" />
            </div>
            <div className="text-sm font-semibold text-gray-900 truncate mb-2" title={l.location_name}>{l.location_name || l.location_id}</div>
            <RatioBar online={l.online} total={l.total} />
          </button>
        ))}
        {(d.locations || []).length === 0 && <div className="text-sm text-gray-600 col-span-full">Keine Standorte zugewiesen.</div>}
      </div>
    </div>
  );
}

function StatusPill({ v }) {
  if (v === "online") return <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-mono font-medium bg-green-500/12 text-green-600 border border-green-500/25"><Wifi className="w-3 h-3" /> online</span>;
  if (v === "offline") return <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-mono font-medium bg-red-500/10 text-red-600 border border-red-500/30"><WifiOff className="w-3 h-3" /> offline</span>;
  return <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-mono font-medium bg-amber-500/10 text-amber-700 border border-amber-500/30">{v || "prüfen"}</span>;
}

function DevicesView({ cfetch, fields }) {
  const [locs, setLocs] = useState([]);
  const [sel, setSel] = useState(() => window.__cpLoc || "");
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => { window.__cpLoc = undefined; cfetch("/cportal/locations").then(setLocs).catch(() => {}); }, [cfetch]);
  useEffect(() => {
    setLoading(true);
    const p = sel ? `/cportal/devices?location_id=${encodeURIComponent(sel)}` : "/cportal/devices";
    cfetch(p).then((d) => setDevices(d.devices || [])).catch(() => {}).finally(() => setLoading(false));
  }, [cfetch, sel]);
  const cols = fields || [];
  const rows = devices.filter((d) => !q || JSON.stringify(d).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Standorte & Geräte</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        <button data-testid="cportal-loc-all" onClick={() => setSel("")} className={`rounded-xl border p-3 text-left transition-all duration-200 ${!sel ? "border-[#c00000]/60 bg-[#c00000]/10 shadow-[0_0_15px_rgba(6,182,212,0.1)]" : "border-gray-200 bg-white/[0.02] hover:bg-white/[0.05]"}`}><div className="text-[11px] text-gray-600 font-mono">Alle</div><div className="text-lg font-bold text-gray-900">{locs.reduce((a, l) => a + (l.device_count || 0), 0)}</div></button>
        {locs.map((l) => (
          <button key={l.location_id} data-testid={`cportal-loc-${l.location_id}`} onClick={() => setSel(l.location_id)} className={`rounded-xl border p-3 text-left transition-all duration-200 ${sel === l.location_id ? "border-[#c00000]/60 bg-[#c00000]/10 shadow-[0_0_15px_rgba(6,182,212,0.1)]" : "border-gray-200 bg-white/[0.02] hover:bg-white/[0.05]"}`}><div className="text-[11px] text-gray-600 font-mono truncate" title={l.location_name}>{l.location_id}</div><div className="text-lg font-bold text-gray-900">{l.device_count}</div></button>
        ))}
      </div>
      <div className="relative"><Search className="w-4 h-4 text-gray-600 absolute left-3.5 top-1/2 -translate-y-1/2" /><input data-testid="cportal-device-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Geräte durchsuchen…" className={`${inp} pl-10`} /></div>
      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-gray-50 shadow-xl">
        {loading ? <div className="p-10 text-center text-gray-600"><Loader2 className="w-5 h-5 animate-spin inline text-[#c00000]" /></div> : (
          <table className="w-full text-left text-sm" data-testid="cportal-device-table">
            <thead className="bg-white/[0.03] text-[11px] font-mono uppercase tracking-wider text-gray-600 border-b border-gray-200"><tr>{cols.map((c) => <th key={c} className="px-4 py-3 whitespace-nowrap">{FIELD_LABELS[c] || c}</th>)}</tr></thead>
            <tbody>
              {rows.map((d, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white/[0.03] transition-colors">{cols.map((c) => <td key={c} className="px-4 py-3 whitespace-nowrap">{cell(c, d[c])}</td>)}</tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={cols.length || 1} className="px-4 py-10 text-center text-gray-600">Keine Geräte gefunden.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-xs text-gray-600">{rows.length} Geräte {sel ? `am Standort ${sel}` : "insgesamt"}</p>
    </div>
  );
}

function cell(key, v) {
  if (key === "status") return <StatusPill v={v} />;
  if (key === "agent_installed") return v === true || v === "true" ? <span className="text-green-600 font-mono text-xs">✓ installiert</span> : <span className="text-gray-600 font-mono text-xs">—</span>;
  if ((key === "last_seen" || key === "first_seen") && v) return <span className="font-mono text-xs text-gray-700" title={String(v)}>{timeAgo(v)}</span>;
  if (v === true) return <span className="text-green-600">✓</span>;
  if (v === false) return <span className="text-gray-600">—</span>;
  return <span className="font-mono text-xs text-gray-700">{v == null || v === "" ? "—" : String(v)}</span>;
}

function TicketsView({ cfetch }) {
  const [tickets, setTickets] = useState(null);
  useEffect(() => { cfetch("/cportal/tickets").then(setTickets).catch(() => setTickets([])); }, [cfetch]);
  if (!tickets) return <Loader2 className="w-5 h-5 animate-spin text-[#c00000]" />;
  const stCls = (s) => s === "closed" ? "bg-gray-500/10 text-gray-600 border-gray-500/30" : s === "in_progress" ? "bg-[#c00000]/10 text-[#c00000] border-[#c00000]/30" : s === "waiting_parts" ? "bg-amber-500/10 text-amber-700 border-amber-500/30" : "bg-green-500/10 text-green-700 border-green-500/30";
  const stLbl = { open: "offen", in_progress: "in Bearbeitung", waiting_parts: "wartet auf Teile", closed: "geschlossen" };
  return (
    <div className="space-y-4">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Meine Anfragen</h2>
      {!tickets.length ? (
        <div className="text-sm text-gray-600 bg-white/[0.03] border border-gray-200 rounded-2xl p-10 text-center"><Ticket className="w-8 h-8 mx-auto mb-2 text-gray-600" />Noch keine Anfragen erstellt.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="cportal-ticket-list">
          {tickets.map((t) => (
            <div key={t.id} className={`${card} p-5 space-y-3 hover:border-gray-300 transition-colors`}>
              <div className="flex items-start justify-between gap-3">
                <span className="text-sm font-semibold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>{t.title}</span>
                <span className={`text-[11px] font-mono px-2.5 py-1 rounded-full border shrink-0 ${stCls(t.status)}`}>{stLbl[t.status] || t.status}</span>
              </div>
              {t.note && <p className="text-xs text-gray-600 leading-relaxed">{t.note}</p>}
              <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono text-gray-600">
                {t.location_id && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-50 border border-gray-200"><MapPin className="w-3 h-3" />{t.location_id}</span>}
                {t.approval_status === "pending" && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/10 text-amber-600 border border-amber-500/30"><Clock className="w-3 h-3" /> wartet auf Freigabe</span>}
                {t.tracking_number && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#c00000]/10 text-[#c00000] border border-[#c00000]/30">Tracking: {t.tracking_number}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewRequestView({ cfetch, onDone }) {
  const [kind, setKind] = useState("");
  const [locs, setLocs] = useState([]);
  const [lid, setLid] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [std, setStd] = useState({ location_name: "", street: "", zip: "", city: "", contact: "", phone: "", email: "" });
  useEffect(() => { cfetch("/cportal/locations").then((l) => { setLocs(l); if (l[0]) setLid(l[0].location_id); }).catch(() => {}); }, [cfetch]);

  const submit = async () => {
    const body = { kind, location_id: lid, description: desc };
    if (kind === "standort") Object.assign(body, std);
    else if (!lid) { toast.error("Bitte Standort wählen."); return; }
    if (kind !== "standort" && !desc.trim()) { toast.error("Bitte beschreiben."); return; }
    if (kind === "standort" && !std.location_name.trim()) { toast.error("Standortname erforderlich."); return; }
    setBusy(true);
    try { await cfetch("/cportal/tickets", { method: "POST", body: JSON.stringify(body) }); toast.success("Anfrage übermittelt."); onDone(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (!kind) return (
    <div className="space-y-4">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Neue Anfrage</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
        {[["problem", "Problem / Support", LifeBuoy, "Ein Gerät funktioniert nicht", "cyan"], ["order", "Bestellung", ShoppingCart, "Gerät / Ersatzteil bestellen", "emerald"], ["return", "Retoure", Undo2, "Etwas an uns zurücksenden", "amber"], ["standort", "Neuer Standort", Building2, "Neue Filiale beantragen", "cyan"]].map(([id, lbl, Icon, sub]) => (
        <button key={id} data-testid={`cportal-kind-${id}`} onClick={() => setKind(id)} className={`${card} p-5 text-left hover:border-[#c00000]/40 hover:bg-gray-50 transition-all duration-200 flex items-start gap-3 group`}>
          <span className="w-11 h-11 rounded-xl bg-[#c00000]/10 border border-[#c00000]/30 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform"><Icon className="w-5 h-5 text-[#c00000]" /></span>
          <div><div className="text-sm font-semibold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>{lbl}</div><div className="text-xs text-gray-600 mt-0.5">{sub}</div></div>
        </button>
      ))}
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl">
      <button onClick={() => setKind("")} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4"><ArrowLeft className="w-4 h-4" /> Zurück</button>
      <div className={`${card} p-6 space-y-4`}>
        {kind !== "standort" && (
          <div><label className="text-[11px] font-mono uppercase tracking-widest text-gray-600 mb-1.5 block">Standort</label>
            <select data-testid="cportal-req-loc" value={lid} onChange={(e) => setLid(e.target.value)} className={inp}>{locs.map((l) => <option key={l.location_id} value={l.location_id} className="bg-gray-50">{l.location_id} — {l.location_name}</option>)}</select></div>
        )}
        {kind === "standort" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[["location_name", "Standortname *", true], ["street", "Straße & Nr. *", true], ["zip", "PLZ *"], ["city", "Ort *"], ["contact", "Ansprechpartner *"], ["phone", "Telefon *"], ["email", "E-Mail *"]].map(([k, lbl, full]) => (
              <label key={k} className={full ? "sm:col-span-2" : ""}><span className="text-[11px] font-mono uppercase tracking-widest text-gray-600 mb-1.5 block">{lbl}</span><input data-testid={`cportal-std-${k}`} value={std[k]} onChange={(e) => setStd({ ...std, [k]: e.target.value })} className={inp} /></label>
            ))}
            <label className="sm:col-span-2"><span className="text-[11px] font-mono uppercase tracking-widest text-gray-600 mb-1.5 block">Bemerkung</span><textarea data-testid="cportal-req-desc" value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} className={inp} /></label>
          </div>
        ) : (
          <div><label className="text-[11px] font-mono uppercase tracking-widest text-gray-600 mb-1.5 block">Beschreibung</label>
            <textarea data-testid="cportal-req-desc" value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} className={inp} placeholder="Bitte beschreiben Sie Ihr Anliegen möglichst genau…" /></div>
        )}
        <button data-testid="cportal-req-submit" onClick={submit} disabled={busy} className="w-full flex items-center justify-center gap-2 bg-[#c00000] hover:bg-[#c00000] text-[#06121a] font-bold rounded-xl px-4 py-3.5 text-sm transition-colors disabled:opacity-50 shadow-[0_0_20px_rgba(6,182,212,0.15)]">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Anfrage senden</button>
      </div>
    </div>
  );
}
